import { z } from 'zod';

// Generic secure storage utility with validation
export class SecureStorage {
  /**
   * Safely parse JSON from localStorage with validation and error handling
   */
  static getItem<T>(key: string, schema: z.ZodSchema<T>, defaultValue: T): T {
    try {
      const item = localStorage.getItem(key);
      if (!item) {
        return defaultValue;
      }

      const parsed = JSON.parse(item);
      const validated = schema.parse(parsed);
      return validated;
    } catch (error) {
      console.warn(`Failed to parse stored data for key "${key}":`, error);
      // Clean up corrupted data
      localStorage.removeItem(key);
      return defaultValue;
    }
  }

  /**
   * Safely store data to localStorage with validation
   */
  static setItem<T>(key: string, value: T, schema?: z.ZodSchema<T>): boolean {
    try {
      // Validate data before storing if schema provided
      if (schema) {
        schema.parse(value);
      }

      const serialized = JSON.stringify(value);
      localStorage.setItem(key, serialized);
      return true;
    } catch (error) {
      console.error(`Failed to store data for key "${key}":`, error);
      return false;
    }
  }

  /**
   * Safely remove item from localStorage
   */
  static removeItem(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn(`Failed to remove item for key "${key}":`, error);
    }
  }

  /**
   * Sanitize string input to prevent potential XSS
   */
  static sanitizeString(input: string): string {
    return input
      .replace(/[<>]/g, '') // Remove basic HTML brackets
      .replace(/javascript:/gi, '') // Remove javascript: protocol
      .replace(/on\w+=/gi, '') // Remove event handlers
      .trim();
  }
}

// Validation schemas for common data types
export const BrandConfigSchema = z.object({
  id: z.string(),
  name: z.string(),
  personalizedName: z.string(),
  primaryColor: z.string(),
  secondaryColor: z.string(),
  industry: z.string(),
  framework: z.enum(['sense', 'span']),
  createdAt: z.string(),
  logoUrl: z.string().optional(),
  avatarUrl: z.string().optional(),
  aiChatText: z.string().optional(),
  userChatText: z.string().optional(),
  aiFollowUpText: z.string().optional(),
});

export const SenseScoreSchema = z.object({
  date: z.string(),
  pillars: z.record(z.string(), z.number()),
  dailyScore: z.number(),
  timestamp: z.string(),
});

export const ReminderDataSchema = z.object({
  id: z.string(),
  message: z.string(),
  scheduledFor: z.date(),
  isActive: z.boolean(),
});